import { createRouter, createWebHistory } from 'vue-router'
import Login from './pages/login/Login.vue'
import Cookies from 'js-cookie'
import store from './core/store/store'
import { pageView } from './plugins/analytics.js'

const routes = [
  {
    path: '/',
    redirect: '/home'
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { 
      requiresGuest: true, 
      showInFooter: false,
      title: 'Login | Deep Ring',
      description: 'Entre na sua conta para acessar o mundo de Deep Ring'
    }
  },
  // Rota de callback de autenticação
  {
    path: '/auth-callback',
    name: 'AuthCallback',
    component: () => import('./pages/login/components/AuthCallback.vue'),
    meta: {
      requiresGuest: true,
      showInFooter: false,
      title: 'Autenticando | Deep Ring',
      description: 'Processando sua autenticação'
    }
  },
  // Rota de registro rápido
  {
    path: '/register',
    name: 'FastRegister',
    component: () => import('./pages/login/FastRegister.vue'),
    meta: { 
      requiresGuest: true, 
      showInFooter: false,
      title: 'Junte-se à Arena | Deep Ring',
      description: 'Crie sua conta rapidamente e comece a lutar no mundo de Deep Ring'
    }
  },
  {
    path: '/home',
    name: 'Home',
    component: () => import('./pages/home/Home.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: true, 
      icon: 'mdi-sword-cross', 
      label: 'Inicio',
      title: 'Início | Deep Ring',
      description: 'Painel principal com informações do seu personagem e atalhos para navegação'
    }
  },
  {
    path: '/create-company',
    name: 'CreateNewCompany',
    component: () => import('./pages/createNewCompany/CreateNewCompany.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: false,
      title: 'Criar Personagem | Deep Ring',
      description: 'Crie seu personagem para começar sua jornada no mundo de Deep Ring'
    }
  },
  {
    path: '/bar',
    name: 'Bar',
    component: () => import('./pages/bar/BarPage.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: true, 
      icon: 'mdi-glass-mug', 
      label: 'Bar',
      title: 'Bar | Deep Ring',
      description: 'Visite o bar para encontrar outros lutadores e receber missões especiais'
    }
  },
  {
    path: '/loja-global',
    name: 'LojaGlobal',
    component: () => import('./pages/lojaGlobal/LojaGlobal.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: true, 
      icon: 'mdi-store', 
      label: 'Mercado',
      title: 'Mercado Global | Deep Ring',
      description: 'Compre e venda itens no mercado global com outros jogadores'
    }
  },
  {
    path: '/ginasio',
    name: 'Ginasio',
    component: () => import('./pages/ginasio/Ginasio.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: true, 
      icon: 'mdi-dumbbell', 
      label: 'Ginásio',
      title: 'Ginásio | Deep Ring',
      description: 'Treine seu personagem para melhorar suas habilidades e estatísticas'
    }
  },
  {
    path: '/city',
    name: 'City',
    component: () => import('./pages/city/CityPage.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: false, 
      icon: 'mdi-map', 
      label: 'Cidade',
      title: 'Cidade | Deep Ring',
      description: 'Explore a cidade e encontre diversos locais para interagir'
    }
  },
  {
    path: '/championships',
    name: 'Championships',
    component: () => import('./pages/championships/ChampionshipsPage.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: true, 
      icon: 'mdi-trophy', 
      label: 'Campeonatos',
      title: 'Campeonatos | Deep Ring',
      description: 'Participe de campeonatos para ganhar prêmios e reconhecimento'
    },
    beforeEnter: (to, from, next) => {
      // Quando navegar de volta do PvP para Championships, força o recarregamento
      if (from.path.includes('/championships/pvp')) {
        // Sinalizamos que precisamos recarregar os campeonatos
        store.commit('championships/SET_SHOULD_RELOAD', true);
      }
      next();
    },
    children: [
      {
        path: 'pvp',
        name: 'PvP',
        component: () => import('./pages/pvp/PvPPage.vue'),
        redirect: '/championships/pvp/list',
        children: [
          {
            path: 'challenge',
            name: 'PvPChallenge',
            component: () => import('./pages/pvp/views/PvPChallenge.vue')
          },
          {
            path: 'list',
            name: 'PvPList',
            component: () => import('./pages/pvp/views/PvPList.vue')
          }
        ]
      }
    ]
  },
  {
    path: '/hospital',
    name: 'Hospital',
    component: () => import('./pages/hospital/HospitalPage.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: true, 
      icon: 'mdi-hospital-box', 
      label: 'Hospital',
      title: 'Hospital | Deep Ring',
      description: 'Recupere a saúde do seu personagem após as lutas'
    }
  },
  {
    path: '/business/:action?',
    name: 'Business',
    component: () => import('./pages/business/BusinessPage.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: true, 
      icon: 'mdi-store-outline', 
      label: 'Negócio',
      customRoute: '/business',
      title: 'Seu Negócio | Deep Ring',
      description: 'Gerencie seu próprio negócio e ganhe dinheiro com minijogos e atividades'
    },
    children: [
      {
        path: '/business/works/fuga',
        name: 'Fuga',
        component: () => import('./pages/business/works/Fuga.vue'),
        meta: { 
          requiresAuth: true, 
          showInFooter: false, 
          isMinigame: true,
          title: 'Minijogo: Fuga | Deep Ring',
          description: 'Teste suas habilidades de fuga neste minijogo desafiador'
        }
      },
      {
        path: '/business/works/block-construction',
        name: 'BlockConstruction',
        component: () => import('./pages/business/works/BlockConstruction.vue'),
        meta: { 
          requiresAuth: true, 
          showInFooter: false, 
          isMinigame: true,
          title: 'Minijogo: Construção de Blocos | Deep Ring',
          description: 'Construa estruturas perfeitas neste minijogo de precisão'
        }
      },
      {
        path: '/business/works/forge-merge',
        name: 'ForgeMerge',
        component: () => import('./pages/business/works/ForgeMerge.vue'),
        meta: { 
          requiresAuth: true, 
          showInFooter: false, 
          isMinigame: true,
          title: 'Minijogo: Forja | Deep Ring',
          description: 'Forje itens valiosos combinando elementos neste minijogo'
        }
      }
    ],
    props: true
  },
  {
    path: '/arena/:matchId',
    name: 'Arena',
    component: () => import('@/pages/arena/Arena.vue'),
    meta: {
      requiresAuth: true,
      showInFooter: false,
      title: 'Arena de Combate | Deep Ring',
      description: 'Enfrente seus adversários nesta arena de combate emocionante'
    }
  },
  // Tournament routes with children
  {
    path: '/tournaments',
    name: 'Tournaments',
    component: () => import('./layouts/TournamentLayout.vue'),
    meta: { 
      requiresAuth: true,
      showInFooter: true,
      icon: 'mdi-tournament',
      label: 'Torneios',
      title: 'Torneios | Deep Ring',
      description: 'Participe de torneios para ganhar prêmios e reconhecimento'
    },
    children: [
      {
        path: '',
        name: 'TournamentsList',
        component: () => import('./pages/tournaments/TournamentsListPage.vue'),
        meta: { 
          title: 'Torneios | Deep Ring',
          description: 'Lista de torneios ativos'
        }
      },
      {
        path: ':id',
        name: 'TournamentDetail',
        component: () => import('./pages/tournaments/TournamentDetailPage.vue'),
        props: true,
        meta: {
          title: 'Detalhes do Torneio | Deep Ring',
          description: 'Informações detalhadas do torneio'
        }
      },
      {
        path: 'match/:id',
        name: 'MatchDetail',
        component: () => import('./pages/tournaments/MatchDetailPage.vue'),
        props: true,
        meta: {
          title: 'Detalhes da Luta | Deep Ring',
          description: 'Informações da luta'
        }
      },
      {
        path: 'matches',
        name: 'MatchesList',
        component: () => import('./pages/tournaments/MatchesListPage.vue'),
        meta: {
          title: 'Lutas | Deep Ring',
          description: 'Lista de lutas programadas'
        }
      }
    ]
  },
  {
    path: '/player/:search?',
    name: 'ProfilePlayer',
    component: () => import('./pages/profilePlayer/ProfilePlayer.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: false,
      title: 'Perfil do Jogador | Deep Ring',
      description: 'Visualize informações detalhadas sobre jogadores'
    }
  },
  {
    path: '/notifications',
    name: 'AllNotification',
    component: () => import('./pages/allNotification/AllNotification.vue'),
    meta: { 
      requiresAuth: true, 
      showInFooter: false,
      title: 'Notificações | Deep Ring',
      description: 'Visualize todas as suas notificações e alertas'
    }
  },
  // Retorno do checkout da Stripe. O success_url e o cancel_url do servidor
  // apontam para cá; sem estas rotas, quem pagava caía num 404 do SPA.
  {
    path: '/payment/success',
    name: 'PaymentSuccess',
    component: () => import('./pages/payment/PaymentSuccess.vue'),
    meta: {
      requiresAuth: true,
      showInFooter: false,
      title: 'Compra concluída | Deep Ring',
      description: 'Confirmação da sua compra de Paralleliums'
    }
  },
  {
    path: '/payment/cancel',
    name: 'PaymentCancel',
    component: () => import('./pages/payment/PaymentCancel.vue'),
    meta: {
      requiresAuth: true,
      showInFooter: false,
      title: 'Compra cancelada | Deep Ring',
      description: 'A compra de Paralleliums foi cancelada'
    }
  },
  // Admin Panel - Protected route
  {
    path: '/panel-admin',
    name: 'AdminPanel',
    component: () => import('./pages/admin/AdminPage.vue'),
    meta: { 
      requiresAuth: true, 
      requiresAdmin: true, // Special admin requirement
      showInFooter: false,
      title: 'Painel Administrativo | Deep Ring',
      description: 'Painel de administração para gerenciamento de torneios e itens premium'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Helper function to check authentication
const isUserAuthenticated = () => {
  return !!Cookies.get('jwt')
}

router.beforeEach(async (to, from, next) => {
  // Set the page title based on the route's meta title
  const pageTitle = to.meta.title || 'Deep Ring'
  document.title = pageTitle
  
  // Track page view for analytics
  pageView(pageTitle, to.fullPath)

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isUserAuthenticated()) {
      next('/login')
    } else {
      if (!store.state.auth.userId) {
        await store.dispatch('auth/tryAutoLogin')
      }
      
      // Check for admin routes
      if (to.matched.some(record => record.meta.requiresAdmin)) {
        const user = store.state.auth.user
        const ADMIN_EMAIL = 'alan.santos01@outlook.com.br'
        
        if (!user || user.email !== ADMIN_EMAIL) {
          // Redirect non-admin users to home with error message
          store.dispatch('SoundController/playSound', { category: 'ui', type: 'error' })
          next('/home')
          return
        }
      }
      
      if (to.path !== '/create-company' && !to.path.startsWith('/panel-admin')) {
        const hasPlayer = await store.dispatch('player/checkPlayerExists')
        if (!hasPlayer) {
          next('/create-company')
          return
        }
      }
      next()
    }
  } 
  else if (to.matched.some(record => record.meta.requiresGuest)) {
    if (isUserAuthenticated()) {
      next('/home')
    } else {
      next()
    }
  } 
  else {
    next()
  }
})

export default router
